import React from "react";
import {
  Box,
  Chip,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useLocation } from "react-router-dom";
import { useProjectSchemas } from "../global-state/useProjectSchemas";
import {
  useConcepts,
  useProjectConcepts,
  mergeProjectPinnedConcepts,
  getDisplayState,
} from "../hooks/useConcepts";
import { useProjectSchedules } from "../projects_v2/project_management/hooks/useProjectSchedules";
import { BuildingChip } from "./BuildingChip";
import { useCurrentProject } from "../global-state/useCurrentProject";
import { AuthContext } from "../auth/Auth";
import { useLoginState } from "../studioxr/auth/LoginState";
import {
  navIcons,
  navSubheaderSx,
  connectorSx,
  navRowSx,
  navRowIconSx,
  navRowTextProps,
} from "./navIcons";

interface ProjectSidebarNavProps {
  projectId: string;
  collapsed: boolean;
  onBeforeNavigate?: () => boolean;
  onNavigate?: () => void;
}

/**
 * Project-mode sidebar (#3544): shown instead of the global nav while inside a
 * project (overview page or a ?project=-scoped list). Back button leaves the
 * project; items are the project's content types with counts. Styled to match
 * the 2a workspace sidebar (rows, icons, subheader, connector line).
 */
export const ProjectSidebarNav = ({
  projectId,
  collapsed,
  onBeforeNavigate,
  onNavigate,
}: ProjectSidebarNavProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = React.useContext(AuthContext);
  const permissions = useLoginState((state) => state.permissions);
  // This project's FF&E schedules (#3559) drive the Schedules item count.
  const { schedules } = useProjectSchedules(
    projectId,
    currentUser?.uid,
    permissions,
  );
  // Real-time concepts, filtered to this project (#3544 Phase 4). The
  // windowed list only sees associations inside the 100-most-recent window,
  // so union it with the targeted current.project_id query (#3670) — which
  // itself degrades to an empty list if its index is missing.
  const { concepts } = useConcepts();
  const { concepts: targetedProjectConcepts } = useProjectConcepts(projectId);
  const projectConcepts = mergeProjectPinnedConcepts(
    targetedProjectConcepts,
    concepts.filter((c) => c.current?.project_id === projectId),
  );
  const projectConceptCount = projectConcepts.length;
  const projectProcessingCount = projectConcepts.filter(
    (c) => getDisplayState(c) === "PENDING",
  ).length;
  const project = useProjectSchemas((s) =>
    s.projects.find((p) => p.id === projectId),
  );
  // Sticky context has the name before the projects store finishes loading.
  const stickyName = useCurrentProject((s) =>
    s.currentProject?.id === projectId ? s.currentProject.name : undefined,
  );
  const projectName = project?.name || stickyName || "Untitled Project";

  const go = (href: string) => {
    if (onBeforeNavigate && !onBeforeNavigate()) return;
    navigate(href);
    onNavigate?.();
  };

  const overviewHref = `/home/projects/${projectId}`;
  // Mockup order: Schedules, Boards, Concepts, Collections.
  const items = [
    // Live (#3559): the project's schedules list. Used to be a `#schedules`
    // anchor on this same overview page, which read as a dead click when you
    // were already on it (#3621) — now a real ?project=-scoped list, like the
    // three items below.
    {
      id: "project-schedules",
      label: "Schedules",
      icon: navIcons.schedules,
      href: `/home/schedules?project=${projectId}`,
      count: schedules.length,
    },
    {
      id: "project-boards",
      label: "Boards",
      icon: navIcons.boards,
      href: `/home/boards?project=${projectId}`,
      count: project?.boards?.length,
    },
    {
      id: "project-concepts",
      label: "Concepts",
      icon: navIcons.concepts,
      href: `/home/concepts?project=${projectId}`,
      count: projectConceptCount,
      building: projectProcessingCount,
    },
    {
      id: "project-collections",
      label: "Collections",
      icon: navIcons.collections,
      href: `/home/collections?project=${projectId}`,
      count: project?.collections?.length,
    },
  ];

  const renderItem = (
    item: {
      id: string;
      label: string;
      icon: React.ReactNode;
      href: string;
      count?: number;
      building?: number;
      disabled?: boolean;
    },
    opts?: { indented?: boolean },
  ) => {
    const isActive =
      !item.disabled && location.pathname + location.search === item.href;
    const button = (
      <ListItemButton
        key={item.id}
        disableRipple
        selected={isActive}
        disabled={item.disabled}
        onClick={() => {
          if (!item.disabled) go(item.href);
        }}
        sx={navRowSx({ collapsed, indented: opts?.indented })}
      >
        <ListItemIcon sx={navRowIconSx(isActive, collapsed)}>
          {item.icon}
        </ListItemIcon>
        {!collapsed && (
          <>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={navRowTextProps}
            />
            {/* Building chip takes the count's slot while any of this
                project's concepts is processing (#3544 mockup) */}
            {(item.building ?? 0) > 0 ? (
              <BuildingChip
                label={`${item.building} concept${
                  item.building === 1 ? "" : "s"
                } processing`}
              />
            ) : (
              typeof item.count === "number" &&
              item.count > 0 && (
                <Typography
                  sx={{
                    ml: "auto",
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#a1a1aa",
                    flexShrink: 0,
                  }}
                >
                  {item.count}
                </Typography>
              )
            )}
            {item.disabled && (
              <Chip
                size="small"
                label="Soon"
                sx={{ height: 18, fontSize: "0.6rem", flexShrink: 0 }}
              />
            )}
          </>
        )}
      </ListItemButton>
    );
    return collapsed ? (
      <Tooltip key={item.id} title={item.label} placement="right">
        {button}
      </Tooltip>
    ) : (
      button
    );
  };

  return (
    <>
      {/* Back + project identity — whole row navigates back, not just the icon. */}
      <Stack
        direction={collapsed ? "column" : "row"}
        alignItems="center"
        spacing={collapsed ? 0 : 1.5}
        onClick={() => go("/home/projects")}
        sx={{ mb: 1, px: collapsed ? 0 : 0.5, cursor: "pointer" }}
      >
        <Tooltip title="Back to projects" placement="right">
          <IconButton
            size="small"
            aria-label="Back to projects"
            sx={{
              backgroundColor: "rgba(15,23,42,0.04)",
              "&:hover": { backgroundColor: "rgba(15,23,42,0.08)" },
            }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {!collapsed && (
          <Box sx={{ minWidth: 0 }}>
            <Typography
              noWrap
              title={projectName}
              sx={{ fontWeight: 700, fontSize: "15px", color: "#1a1a1a" }}
            >
              {projectName}
            </Typography>
            {project?.type && (
              <Typography
                noWrap
                variant="caption"
                sx={{ color: "text.secondary" }}
              >
                {project.type}
              </Typography>
            )}
          </Box>
        )}
      </Stack>

      <List disablePadding>
        {renderItem({
          id: "project-overview",
          label: "Overview",
          icon: navIcons.overview,
          href: overviewHref,
        })}
      </List>

      {!collapsed && (
        <ListSubheader disableSticky sx={navSubheaderSx}>
          THIS PROJECT
        </ListSubheader>
      )}
      {/* Indented group under the section label, per the 2a pattern — the
          connector wraps only the rows; the outer box absorbs leftover
          height. */}
      <Box sx={{ flexGrow: 1 }}>
        <Box sx={{ position: "relative" }}>
          {!collapsed && <Box sx={connectorSx("9px")} />}
          <List disablePadding>
            {items.map((item) => renderItem(item, { indented: true }))}
          </List>
        </Box>
      </Box>

      {/* People-with-access moved to the overview's Team section (DS2). */}
    </>
  );
};
